<template>
  <div class="cms-review-form">
    <cms-form-header
      :title="isEditing ? $t('cms.reviews.editReview') : $t('cms.reviews.createReview')"
      :disabled="isSaving || isLoadingReview"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    />

    <cms-form-layout>
      <form @submit.prevent="handleSubmit">
        <cms-form-section :title="$t('cms.reviews.generalInfo')">
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <div class="cms-form-grid">
                <cms-input
                  v-model="getTranslation(locale).title"
                  :label="`${$t('cms.reviews.reviewTitle')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-editor
                  v-model="getTranslation(locale).content"
                  :label="`${$t('cms.reviews.content')} (${locale.toUpperCase()})`"
                />
              </div>
            </template>
          </cms-translation-tabs>
        </cms-form-section>

        <cms-form-section :title="$t('cms.reviews.additionalInfo')">
          <div class="cms-form-grid">
            <cms-input
              v-model="form.author_name"
              :label="$t('cms.reviews.authorName')"
              :required="true"
            />
            <cms-input
              v-model="form.author_email"
              :label="$t('cms.reviews.authorEmail')"
              type="email"
            />
            <div class="cms-form-group">
              <label>{{ $t('cms.reviews.rating') }}</label>
              <v-star-rating
                v-model="form.rating"
                icon="star"
                icon-size="md"
                :name="`review-rating-${reviewId || 'new'}`"
                :required="true"
                :ratings="[1, 2, 3, 4, 5]"
              />
              <p class="cms-form-help">{{ form.rating ? `${form.rating} ${form.rating === 1 ? 'Star' : 'Stars'}` : 'Select a rating' }}</p>
            </div>
            <div class="cms-form-group">
              <label>{{ $t('cms.reviews.date') }}</label>
              <Datepicker
                v-model="reviewDateValue"
                :enable-time-picker="false"
                format="dd-MM-yyyy"
                :month-change-on-scroll="false"
                auto-apply
                class="cms-datepicker"
              />
            </div>
          </div>
        </cms-form-section>
      </form>

      <template #sidebar>
        <cms-sidebar>
          <cms-sidebar-section :title="$t('cms.reviews.mediaSection')">
            <template v-if="reviewId">
              <cms-image-upload
                ref="featuredImageUploadRef"
                v-model="form.featured_image"
                :label="$t('cms.reviews.featuredImage')"
                :help-text="$t('cms.reviews.featuredImageHelp')"
                :initial-media="featuredUploaderInitialMedia"
                @uploaded="handleFeaturedUploaded"
                @removed="handleFeaturedRemoved"
              />
              <div class="cms-media-actions" :class="{ 'is-media-saving': isSyncingFeaturedMedia }">
                <cms-button
                  variant="secondary"
                  size="sm"
                  :disabled="!canSaveFeaturedMedia || isSyncingFeaturedMedia"
                  @click="saveFeaturedMedia"
                >
                  <v-icon icon="upload" size="sm" />
                  {{ isSyncingFeaturedMedia ? $t('general.button.saving') : $t('cms.reviews.uploadFeatured') }}
                </cms-button>
              </div>

              <div class="cms-sidebar-featured-alt">
                <cms-input
                  v-model="getTranslation(currentLanguage).featured_image_alt"
                  :label="`${$t('cms.reviews.featuredImageAlt')} (${currentLanguage.toUpperCase()})`"
                  :help-text="$t('cms.reviews.featuredImageAltHelp')"
                  :full-width="true"
                  :disabled="isSaving || isLoadingReview"
                  :show-tooltip-on-hover="false"
                />
              </div>
            </template>
            <template v-else>
              <p class="cms-form-help cms-media-disabled">
                {{ $t('cms.activities.mediaUploadRequiresCreation') }}
              </p>
            </template>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.reviews.relationshipsSection')">
            <cms-select
              v-model="activitySelectValue"
              :label="$t('cms.reviews.activity')"
              :options="activityOptions"
              :placeholder="$t('cms.reviews.activityPlaceholder')"
              :full-width="true"
              :disabled="isEditing && !!form.cms_activity_id"
            />
            <p class="cms-form-help">{{ $t('cms.reviews.activityHelp') }}</p>
          </cms-sidebar-section>
        </cms-sidebar>
      </template>
    </cms-form-layout>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import type { ReviewForm, ReviewTranslation } from '@/types/cms';
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsButton,
  CmsEditor,
  CmsSidebar,
  CmsSidebarSection,
  CmsTranslationTabs,
  CmsSelect,
  CmsImageUpload,
} from '@/components/cms';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';
import { hasPendingCmsMediaSave, persistCmsMediaAttachment } from '@/helpers/cmsMediaUpload';
import { runCmsMediaSync } from '@/helpers/cmsMediaSaveFlow';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VStarRating from '@/components/vendor/VStarRating.vue';

interface SelectOption {
  value: number | string;
  label: string;
}

export default defineComponent({
  name: 'TheCmsReviewForm',
  components: {
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsButton,
    CmsEditor,
    CmsSidebar,
    CmsSidebarSection,
    CmsTranslationTabs,
    CmsSelect,
    CmsImageUpload,
    Datepicker,
    VIcon,
    VStarRating,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const reviewId = ref(route.params.id ? Number(route.params.id) : null);
    const isEditing = computed(() => reviewId.value !== null);

    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const createEmptyTranslation = (locale: string): ReviewTranslation => ({
      locale,
      title: '',
      content: '',
      featured_image_alt: '',
    });

    const form = reactive<ReviewForm>({
      author_name: '',
      author_email: '',
      rating: 0,
      review_date: '',
      cms_activity_id: null,
      featured_image: '',
      status: 'pending',
      translations: locales.value.map((locale) => createEmptyTranslation(locale)),
    });

    const activities = ref<SelectOption[]>([]);
    const activityOptions = computed(() => [
      { value: '', label: t('cms.reviews.activityPlaceholder') },
      ...activities.value,
    ]);

    const activitySelectValue = computed<string | number>({
      get: () => {
        if (form.cms_activity_id === null || typeof form.cms_activity_id === 'undefined') {
          return '';
        }
        return form.cms_activity_id;
      },
      set: (value) => {
        if (value === '' || value === null) {
          form.cms_activity_id = null;
          return;
        }
        if (typeof value === 'number') {
          form.cms_activity_id = value;
          return;
        }
        const parsed = Number(value);
        form.cms_activity_id = Number.isNaN(parsed) ? null : parsed;
      },
    });

    const getTranslation = (locale: string): ReviewTranslation => {
      let translation = form.translations.find((entry) => entry.locale === locale);
      if (!translation) {
        translation = createEmptyTranslation(locale);
        form.translations.push(translation);
      }
      return translation;
    };

    const stripHtmlContent = (value: string | null | undefined) => {
      if (!value) {
        return '';
      }
      return value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    };

    const requiredTranslationFields: Array<keyof ReviewTranslation> = ['title', 'content'];

    const translationsAreComplete = computed(() => {
      return form.translations.some((translation) =>
        requiredTranslationFields.every((field) => {
          const value = translation[field];
          if (typeof value === 'string') {
            const normalized = field === 'content' ? stripHtmlContent(value) : value.trim();
            return normalized.length > 0;
          }
          return false;
        }),
      );
    });

    const validateForm = () => {
      if (!translationsAreComplete.value) {
        const missingFields = requiredTranslationFields
          .map((field) => t(`cms.reviews.${field === 'title' ? 'reviewTitle' : field}`))
          .join(', ');
        toast.error(
          t('cms.stories.translationValidationError', {
            fields: missingFields,
            language: t('cms.stories.translationGenericLanguage'),
          }),
        );
        return false;
      }
      if (form.rating === 0 || form.rating < 1 || form.rating > 5) {
        toast.error(t('cms.reviews.rating') + ' is required');
        return false;
      }
      if (!form.review_date) {
        toast.error(t('cms.reviews.date') + ' is required');
        return false;
      }
      return true;
    };

    const isSaving = ref(false);
    const isLoadingReview = ref(false);

    const extractListData = (response: any) => {
      if (!response) return [];
      const candidates = [response?.data?.data, response?.data, response?.data?.data?.data, response];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const extractReviewData = (response: any) => {
      if (!response) return null;
      const candidates = [response?.data?.data, response?.data, response];
      for (const candidate of candidates) {
        if (!candidate) continue;
        if (candidate.attributes && typeof candidate.attributes === 'object') {
          return {
            id: candidate.id ?? candidate.attributes.id ?? null,
            ...candidate.attributes,
            relationships: candidate.relationships,
          };
        }
        if (candidate.id || candidate.translations) {
          return candidate;
        }
      }
      return null;
    };

    const toInputDate = (value?: string | null) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    };

    const reviewDateValue = computed<Date | null>({
      get: () => {
        if (!form.review_date) return null;
        const date = new Date(form.review_date);
        return Number.isNaN(date.getTime()) ? null : date;
      },
      set: (value: Date | null) => {
        if (value && value instanceof Date && !Number.isNaN(value.getTime())) {
          form.review_date = value.toISOString().split('T')[0];
        } else {
          form.review_date = '';
        }
      },
    });

    const mapReviewToForm = (review: any) => {
      form.author_name = review.author_name || review.authorName || '';
      form.author_email = review.author_email || review.authorEmail || '';
      form.rating = review.rating || 0;
      form.status = review.status || 'pending';
      form.review_date = toInputDate(review.review_date || review.reviewDate);

      const activityRelation = normalizeResource(extractRelationship(review, 'activity')) || review.activity || {};
      form.cms_activity_id = activityRelation.id || review.cms_activity_id || review.activityId || null;

      mapReviewMediaCollections(review);
    };

    const applyTranslationsFromReview = (review: any, locale: string) => {
      const translations = extractRelationship(review, 'translations');
      const normalized = normalizeResource(translations);
      const translation = Array.isArray(normalized) ? normalized[0] : normalized || review.translations?.[0];
      if (!translation) {
        return;
      }
      const target = getTranslation(locale);
      target.title = translation.title || '';
      target.content = translation.content || '';
      target.featured_image_alt = translation.featured_image_alt ?? translation.featuredImageAlt ?? '';
    };

    const buildPayload = () => {
      const translationsPayload = form.translations
        .map((translation) => ({
          locale: translation.locale,
          title: translation.title?.trim(),
          content: translation.content,
          featured_image_alt: translation.featured_image_alt?.trim() || '',
        }))
        .filter((translation) =>
          requiredTranslationFields.every((field) => {
            const value = translation[field];
            if (typeof value === 'string') {
              const normalized = field === 'content' ? stripHtmlContent(value) : value.trim();
              return normalized.length > 0;
            }
            return false;
          }),
        );

      return {
        author_name: form.author_name,
        author_email: form.author_email,
        rating: form.rating,
        status: form.status,
        review_date: form.review_date || null,
        cms_activity_id: form.cms_activity_id,
        translations: translationsPayload,
      };
    };

    const extractReviewId = (response: any): number | null => {
      if (!response) return null;
      if (typeof response.id === 'number') {
        return response.id;
      }
      if (typeof response?.data?.id === 'number') {
        return response.data.id;
      }
      if (typeof response?.data?.data?.id === 'number') {
        return response.data.data.id;
      }
      return null;
    };

    const loadActivities = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          perPage: 100,
          order: { key: 'updated_at', direction: 'desc' },
        });
        const list = extractListData(response);
        activities.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          return {
            value: normalized.id,
            label: normalized.title || normalized.name || `#${normalized.id}`,
          };
        });
      } catch (error) {
        console.error('Failed to load activities', error);
      }
    };

    // Featured Image Management
    const featuredImageUploadRef = ref<any>(null);
    const featuredUploaderInitialMedia = ref<any[]>([]);
    const featuredMediaPayload = ref<any[] | null>(null);
    const pendingFeaturedDeletion = ref<any | null>(null);
    const isSyncingFeaturedMedia = ref(false);

    const CMS_MEDIA_MODEL_TYPE = 'cms_review';
    const FEATURED_MEDIA_COLLECTION = 'cms_review_featured';
    const MEDIA_DRIVER = 's3';

    const cloneMediaItems = (items: any[] = []) => items.map((item) => JSON.parse(JSON.stringify(item)));

    const getCollectionName = (item: any) => item?.collection_name || item?.collectionName || '';
    const getCustomProperties = (item: any) => item?.custom_properties || item?.customProperties || {};
    const getOrderColumn = (item: any) => item?.order_column ?? item?.orderColumn;

    const getMediaUrl = (item: any) => {
      if (!item) return '';
      const attributes = item.attributes || {};
      return (
        item.storage_url ||
        item.storageUrl ||
        attributes.storage_url ||
        attributes.storageUrl ||
        item.original_url ||
        item.originalUrl ||
        attributes.original_url ||
        attributes.originalUrl ||
        item.preview_url ||
        attributes.preview_url ||
        ''
      );
    };

    const getMediaPreviewUrl = (item: any, fallback: string) => {
      const attributes = item?.attributes || {};
      return (
        item.preview_url ||
        item.previewUrl ||
        attributes.preview_url ||
        attributes.previewUrl ||
        fallback
      );
    };

    const normalizeMediaForUploader = (item: any, fallbackOrder = 1) => {
      if (!item) return null;
      const customProps = getCustomProperties(item);
      const order = customProps?.order ?? getOrderColumn(item) ?? fallbackOrder;
      const storageUrl = getMediaUrl(item);
      const previewUrl = getMediaPreviewUrl(item, storageUrl);

      return {
        ...item,
        order,
        order_column: order,
        original_url: storageUrl,
        storage_url: storageUrl,
        preview_url: previewUrl,
        attributes: {
          ...(item.attributes || {}),
          uuid: item.uuid,
          name: item.name || `media-${order}`,
          order_column: order,
          collection_name: getCollectionName(item),
          original_url: storageUrl,
          preview_url: previewUrl,
          isExisting: true,
          urls: {
            ...(item.attributes?.urls || {}),
            original: storageUrl,
            preview: previewUrl,
          },
        },
      };
    };

    const extractMediaItems = (review: any) => {
      const mediaRelationship = extractRelationship(review, 'media');
      const normalizedRelationship = normalizeResource(mediaRelationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (Array.isArray(review.media)) {
        return review.media;
      }
      return [];
    };

    const mapReviewMediaCollections = (review: any) => {
      const mediaItems = extractMediaItems(review)
        .map((item: any) => normalizeResource(item) || item)
        .filter((item: any) => !!item && (item.uuid || item.id));

      const featuredItems = mediaItems
        .filter((item: any) => getCollectionName(item) === FEATURED_MEDIA_COLLECTION)
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean);

      featuredUploaderInitialMedia.value = cloneMediaItems(featuredItems);
      pendingFeaturedDeletion.value = null;

      const featuredUrl = getMediaUrl(featuredItems[0]);
      form.featured_image = featuredUrl || '';
    };

    const deleteMediaItemsByIds = async (ids: Array<number | null | undefined>) => {
      const uniqueIds = Array.from(
        new Set((ids || []).filter((id): id is number => typeof id === 'number' && id > 0)),
      );
      if (!uniqueIds.length) {
        return;
      }
      await Promise.all(uniqueIds.map((id) => api.media.delete(MEDIA_DRIVER, id)));
    };

    const buildMediaConfirmEntries = (mediaItems: any[] = [], collection: string) => {
      return (Array.isArray(mediaItems) ? mediaItems : [])
        .map((item: any, index: number) => {
          const attributes = item?.attributes ?? {};
          if (attributes.isExisting) {
            return null;
          }
          const uuid = attributes.uuid ?? item.uuid;
          if (!uuid) {
            return null;
          }
          const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;
          return {
            uuid,
            name: attributes.name ?? item.name ?? `media-${order}`,
            order,
            collection,
          };
        })
        .filter((entry): entry is { uuid: string; name: string; order: number; collection: string } => !!entry);
    };

    const refreshReviewMediaState = async () => {
      if (!agencySlug.value || !reviewId.value) {
        return;
      }
      try {
        const response = await api.reviewService.getCmsReview(agencySlug.value, reviewId.value);
        const reviewData = extractReviewData(response);
        if (reviewData) {
          mapReviewMediaCollections(reviewData);
        }
      } catch (error) {
        console.error('Error refreshing review media', error);
      }
    };

    const confirmFeaturedMedia = async () => {
      if (!reviewId.value || isSyncingFeaturedMedia.value || isLoadingReview.value) {
        return;
      }
      const payload = featuredMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, FEATURED_MEDIA_COLLECTION);
      const hasPendingDeletion = !!pendingFeaturedDeletion.value?.id;
      if (!hasUploads && !hasPendingDeletion) {
        return;
      }
      try {
        await runCmsMediaSync(isSyncingFeaturedMedia, async () => {
          if (hasPendingDeletion) {
            await deleteMediaItemsByIds([pendingFeaturedDeletion.value.id]);
            pendingFeaturedDeletion.value = null;
          } else if (hasUploads && featuredUploaderInitialMedia.value.length > 0) {
            await deleteMediaItemsByIds(featuredUploaderInitialMedia.value.map((item: any) => item.id));
          }
          if (hasUploads) {
            await persistCmsMediaAttachment({
              api,
              driver: MEDIA_DRIVER,
              agencySlug: agencySlug.value!,
              mediaItems: payload,
              modelType: CMS_MEDIA_MODEL_TYPE,
              modelId: reviewId.value,
              collection: FEATURED_MEDIA_COLLECTION,
            });
          }
          featuredMediaPayload.value = null;
          featuredImageUploadRef.value?.clearMedia?.();
          await refreshReviewMediaState();
        });
        toast.success(t('cms.activities.featuredMediaSaveSuccess'));
      } catch (error) {
        console.error('Error saving featured media', error);
        toast.error(t('general.errorMessage'));
      }
    };

    const persistPendingMediaChanges = async () => {
      await confirmFeaturedMedia();
    };

    const handleFeaturedUploaded = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };

    const handleFeaturedRemoved = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
      if ((!media || media.length === 0) && featuredUploaderInitialMedia.value.length > 0) {
        pendingFeaturedDeletion.value = featuredUploaderInitialMedia.value[0];
      }
    };

    const canSaveFeaturedMedia = computed(
      () =>
        !!reviewId.value &&
        featuredMediaPayload.value !== null &&
        !isSyncingFeaturedMedia.value &&
        !isSaving.value &&
        !isLoadingReview.value,
    );

    const saveFeaturedMedia = async () => {
      await confirmFeaturedMedia();
    };

    const loadReview = async () => {
      if (!agencySlug.value || !reviewId.value) {
        return;
      }
      isLoadingReview.value = true;
      try {
        const response = await api.reviewService.getCmsReview(agencySlug.value, reviewId.value, {
          headers: {
            'Accept-Language': currentLanguage.value,
          },
        });
        const reviewData = extractReviewData(response);
        if (reviewData) {
          mapReviewToForm(reviewData);
          for (const locale of locales.value) {
            applyTranslationsFromReview(reviewData, locale);
          }
        }
      } catch (error) {
        console.error('Failed to load review', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoadingReview.value = false;
      }
    };

    const persistReview = async (publish = false) => {
      if (!agencySlug.value) return;
      if (!validateForm()) {
        return;
      }
      const payload = buildPayload();
      isSaving.value = true;
      try {
        if (reviewId.value) {
          await api.reviewService.updateCmsReview(agencySlug.value, reviewId.value, payload);
        } else {
          const response = await api.reviewService.createCmsReview(agencySlug.value, payload);
          const newId = extractReviewId(response);
          if (newId) {
            reviewId.value = newId;
            try {
              await router.replace({ name: 'cms.reviews.edit', params: { id: newId } });
            } catch (error) {
              console.warn('Router replace failed', error);
            }
            await loadReview();
          }
        }
        await persistPendingMediaChanges();
        toast.success(publish ? t('cms.reviews.publishSuccess') : t('general.shared.savedChanges'));
      } catch (error: any) {
        console.error('Error saving review', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isSaving.value = false;
      }
    };

    const saveDraft = () => {
      persistReview(false);
    };

    const saveAndPublish = () => {
      persistReview(true);
    };

    const handleSubmit = () => {
      saveDraft();
    };

    const goBack = () => {
      router.push({ name: 'cms.reviews' });
    };

    watch(
      () => route.params.id,
      (newId) => {
        reviewId.value = newId ? Number(newId) : null;
        if (reviewId.value) {
          loadReview();
        } else {
          form.rating = 0;
          form.review_date = '';
          form.cms_activity_id = null;
          form.featured_image = '';
          form.translations = locales.value.map((locale) => createEmptyTranslation(locale));
          featuredUploaderInitialMedia.value = [];
          featuredMediaPayload.value = null;
          pendingFeaturedDeletion.value = null;
        }
      },
      { immediate: true },
    );

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          loadActivities();
          if (reviewId.value) {
            loadReview();
          }
        }
      },
      { immediate: true },
    );

    return {
      form,
      isEditing,
      isSaving,
      isLoadingReview,
      languages,
      currentLanguage,
      reviewDateValue,
      getTranslation,
      activityOptions,
      activitySelectValue,
      featuredImageUploadRef,
      featuredUploaderInitialMedia,
      canSaveFeaturedMedia,
      saveFeaturedMedia,
      handleFeaturedUploaded,
      handleFeaturedRemoved,
      saveDraft,
      saveAndPublish,
      handleSubmit,
      goBack,
      reviewId,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

// ============================================
// VIEW-SPECIFIC STYLES
// ============================================
// Only styles unique to this view

.cms-review-form {
  color: $cms-text-primary;
}

// Review actions (unique to this view)
.cms-review-actions {
  display: flex;
  justify-content: flex-end;
  gap: $cms-spacing-sm;
  margin-top: $cms-spacing-lg;
}

// Media actions (unique to this view)
.cms-media-actions {
  margin-top: $cms-spacing-xs;
  display: flex;
  justify-content: flex-end;
}

.cms-media-disabled {
  color: $cms-text-secondary;
}

.cms-datepicker {
  width: 100%;

  :deep(.dp__input_wrap) {
    width: 100%;
  }

  :deep(.dp__input) {
    width: 100%;
    padding: $cms-spacing-sm $cms-spacing-md;
    border: 1px solid $cms-border-color-light;
    border-radius: $cms-radius-md;
    font-size: $cms-font-size-base;
    color: $cms-text-primary;
    background-color: $cms-background-white;
    transition: all $cms-transition-base;

    &:hover {
      border-color: rgba($cms-primary-color, 0.3);
    }

    &:focus {
      outline: none;
      border-color: $cms-primary-color;
      box-shadow: 0 0 0 3px rgba($cms-primary-color, 0.1);
    }
  }
}
</style>

